import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';

// Define the shape of the context
export interface ChatInputContextType {
  // Actions
  setInputValue: (value: string) => void;
  registerInputSetter: (setter: (value: string) => void) => void;
}

// Create the context with a default value
export const ChatInputContext = createContext<ChatInputContextType | undefined>(undefined);

// Define props for the provider
interface ChatInputProviderProps {
  children: ReactNode;
}

// Create the provider component
export const ChatInputProvider: React.FC<ChatInputProviderProps> = ({ children }) => {
  // We don't actually store the input value here, just provide the setter function
  // The actual state remains in the Thread component

  // Initialize with a default no-op function to prevent warnings
  const [setInputCallback, setSetInputCallback] = useState<(value: string) => void>(() => {
    return (value: string) => {
      // No-op until setter is registered
    };
  });

  // Proxy function to set the input value
  const setInputValue = useCallback((value: string) => {
    setInputCallback(value);
  }, [setInputCallback]);

  // Function to register the real setter from Thread component
  const registerInputSetter = useCallback((setter: (value: string) => void) => {
    setSetInputCallback(() => setter);
  }, []);

  // Context value object
  const contextValue: ChatInputContextType = {
    // Actions
    setInputValue,
    registerInputSetter
  };

  return (
    <ChatInputContext.Provider value={contextValue}>
      {children}
    </ChatInputContext.Provider>
  );
};

// Custom hook to use the chat input context
export const useChatInput = (): ChatInputContextType => {
  const context = useContext(ChatInputContext);

  if (context === undefined) {
    throw new Error('useChatInput must be used within a ChatInputProvider');
  }

  return context;
};
